import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Sparkles, Shield, Scale, Rocket, Lock, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSlip } from "@/context/SlipContext";
import { SPORT_META, TIER_LIMITS } from "@/lib/odds";
import { useDataSource } from "@/lib/dataSource";
import { ParlaySlip } from "@/components/ParlaySlip";
import { PickCard } from "@/components/PickCard";
import { UpgradeGate } from "@/components/TierBadge";

const RISKS = [
  { key: "safe", label: "Safe", icon: Shield, text: "High-probability legs, modest payout" },
  { key: "balanced", label: "Balanced", icon: Scale, text: "Edge-weighted, +400 to +900 target" },
  { key: "longshot", label: "Longshot", icon: Rocket, text: "Max payout with positive edge" },
];
const EXAMPLES = ["Same game parlay with player props on tonight's biggest football game", "4-leg alt passing, rushing and receiving yard lines in football games", "5-leg MLB player props parlay, hits and strikeouts", "4-leg NFL + college football spreads, safe", "3-leg soccer same game parlay on the Man City match", "6-leg cross-sport longshot"];

export default function Builder() {
  const ds = useDataSource();
  const { user } = useAuth();
  const { legs, replace } = useSlip();
  const premium = user.tier === "premium";
  const limits = TIER_LIMITS[user.tier];
  const [numLegs, setNumLegs] = useState(3);
  const [risk, setRisk] = useState("balanced");
  const [sports, setSports] = useState([]);
  const [request, setRequest] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (user.tier === "free") return <Navigate to="/builder-locked" replace />;

  const toggleSport = (k) => setSports((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const optimize = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = premium ? { legs: numLegs, risk, sports, request } : { legs: numLegs, risk: "balanced", sports: [], request: "" };
      const { data } = await api.post("/parlays/optimize", body);
      replace(data.picks, { title: data.title, strategy: data.strategy });
      toast.success(`AI built "${data.title}" (${data.picks.length} legs${data.mode === "props" ? " · live prop ladders" : ""})`);
      if (data.note) toast.message(data.note);
    } catch (e) {
      setErr(errMsg(e));
      toast.error(errMsg(e), { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="builder-page">
      <p className="label-caps text-emerald-400">Laboratory</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Parlay Builder</h1>
      <p className="mt-2 max-w-xl text-sm text-slate-400">Tell the AI how many legs you want and it builds the ticket. {premium ? "Premium: up to 10 legs, any risk profile, any sport, or describe exactly what you want." : `Basic: balanced builds up to ${limits.optMaxLegs} legs. Upgrade for custom requests, risk profiles and up to 10 legs.`}</p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_400px]">
        <section className="space-y-6">
          <div className="glass-card p-6" data-testid="ai-optimizer-panel">
            <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Sparkles size={20} className="text-cyan-400" /> Build on Request</h2>
            <div className="mt-6">
              <div className="flex items-center justify-between"><label className="label-caps">Legs</label><span data-testid="optimizer-legs-value" className="font-mono-num text-lg font-bold text-emerald-400">{numLegs}</span></div>
              <input data-testid="optimizer-legs-slider" type="range" min="2" max={limits.optMaxLegs} value={numLegs} onChange={(e) => setNumLegs(Number(e.target.value))} className="mt-2 w-full accent-emerald-500" />
              <div className="flex justify-between text-[10px] text-slate-500"><span>2</span><span>{limits.optMaxLegs}{!premium && " (Premium: 10)"}</span></div>
            </div>

            <div className="mt-6">
              <label className="label-caps flex items-center gap-2">Risk profile {!premium && <Lock size={11} className="text-amber-400" />}</label>
              <div className="mt-2 grid gap-2 sm:grid-cols-3">
                {RISKS.map((r) => {
                  const disabled = !premium && r.key !== "balanced";
                  return (
                    <button key={r.key} data-testid={`optimizer-risk-${r.key}`} disabled={disabled} onClick={() => setRisk(r.key)} className={`rounded-xl border p-3 text-left transition-[border-color,background-color,transform] duration-200 ${disabled ? "cursor-not-allowed border-[#26334D]/60 opacity-40" : "hover:-translate-y-0.5"} ${risk === r.key ? "border-emerald-500 bg-emerald-500/10" : "border-[#26334D] bg-[#0B0E14] hover:border-slate-500"}`}>
                      <r.icon size={16} className={risk === r.key ? "text-emerald-400" : "text-slate-400"} />
                      <p className="mt-2 text-sm font-bold text-white">{r.label}</p>
                      <p className="text-xs text-slate-500">{r.text}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {premium && (
              <>
                <div className="mt-6">
                  <label className="label-caps">Sports <span className="text-slate-600">(none = all)</span></label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {Object.entries(SPORT_META).filter(([k]) => !(ds.hidden || []).includes(k)).map(([k, m]) => (
                      <button key={k} data-testid={`optimizer-sport-${k}`} onClick={() => toggleSport(k)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${sports.includes(k) ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />{m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-6">
                  <label className="label-caps flex items-center gap-2"><MessageSquareText size={12} /> Custom request <span className="text-slate-600">(optional)</span></label>
                  <textarea data-testid="optimizer-request-input" value={request} onChange={(e) => setRequest(e.target.value)} rows={2} maxLength={400} placeholder="e.g. 5-leg NBA player props parlay, mostly points overs, keep it under +1200" className="input-dark mt-2 resize-none" />
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {EXAMPLES.map((ex) => <button key={ex} data-testid="optimizer-example-chip" onClick={() => setRequest(ex)} className="rounded-full border border-[#26334D] px-2.5 py-1 text-[11px] text-slate-400 transition-colors hover:border-cyan-500/50 hover:text-cyan-300">{ex}</button>)}
                  </div>
                </div>
              </>
            )}
            {!premium && (
              <p data-testid="builder-basic-notice" className="mt-5 flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200"><Lock size={12} /> Custom requests, sport filters, player-prop pools and Safe/Longshot profiles are Premium. <a href="/pricing" className="ml-auto font-bold underline">Upgrade</a></p>
            )}
            {err && <p data-testid="optimizer-error" className="mt-5 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-200">{err}</p>}
            <button data-testid="parlay-ai-optimize-btn" onClick={optimize} disabled={busy} className="btn-primary mt-7 w-full !py-3.5">
              <Sparkles size={16} className={busy ? "animate-spin" : ""} /> {busy ? "Claude is building your ticket…" : `Build ${numLegs}-Leg Parlay`}
            </button>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Legs in your slip</h2>
            {legs.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500" data-testid="builder-empty">Nothing yet. Add picks from the <a href="/dashboard" className="text-emerald-400 underline">Daily Picks</a> or build one above.</p>
            ) : (
              <div className="mt-4 grid gap-4 md:grid-cols-2">{legs.map((p, i) => <PickCard key={p.pick_id} pick={p} index={i} compact />)}</div>
            )}
          </div>
        </section>
        <div className="lg:sticky lg:top-24 lg:self-start"><ParlaySlip full /></div>
      </div>
    </main>
  );
}

export function BuilderLocked() {
  return (
    <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="builder-locked-page">
      <p className="label-caps text-emerald-400">Laboratory</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Parlay Builder</h1>
      <div className="mt-8">
        <UpgradeGate testId="builder-upgrade-gate" required="Basic or Premium" title="The builder is members-only" description="Free accounts can browse the daily slate, but building and saving parlays requires Basic (balanced builds up to 3 legs) or Premium (custom requests, any risk profile, player props, up to 10 legs)." />
      </div>
    </main>
  );
}
